import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Paging, FilterRow, HeaderFilter, Sorting } from 'devextreme-react/data-grid';
import { Popup } from 'devextreme-react/popup';
import { Button as DxButton } from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import CheckBox from 'devextreme-react/check-box';
import NumberBox from 'devextreme-react/number-box';
import TagBox from 'devextreme-react/tag-box';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import ValidationGroup from 'devextreme-react/validation-group';
import { Validator, RequiredRule, PatternRule, StringLengthRule, EmailRule, AsyncRule, CustomRule } from 'devextreme-react/validator';
import { useAppContext } from '../shared/AppContext';
import { UserDto } from '../../dtos/UserDto';
import { UserRole } from '../../dtos/UserRole';
import { ExternalUserRole } from '../../dtos/ExternalUserRole';
import { GrantOptionDto } from '../../dtos/GrantOptionDto';
import { ExternalUserGrantAssignmentDto } from '../../dtos/ExternalUserGrantAssignmentDto';
import {
    getUsers, createUser, updateUser, usernameExists, emailExists,
    getGrantOptions, getUserGrantAssignments, createExternalUser, updateExternalUser
} from '../../api/v2/UserManagementApi';

const roleOptions = [
    { value: UserRole.PO, label: 'PO' },
    { value: UserRole.ORF_DTR, label: 'ORF-DTR' },
    { value: UserRole.ORF_DEP, label: 'ORF-DEP' },
    { value: UserRole.GMS, label: 'GMS' },
    { value: UserRole.SU, label: 'Superuser' },
    { value: UserRole.EXTERNAL_USER, label: 'External User' },
];

const roleLabels: Record<number, string> = {
    [UserRole.PO]: 'PO',
    [UserRole.ORF_DTR]: 'ORF-DTR',
    [UserRole.ORF_DEP]: 'ORF-DEP',
    [UserRole.GMS]: 'GMS',
    [UserRole.SU]: 'Superuser',
    [UserRole.PI]: 'PI',
    [UserRole.SO]: 'SO',
    [UserRole.EXTERNAL_USER]: 'External User',
};

// PI and SO accounts are created and refreshed from eRA at login, so the only
// thing worth changing here is whether the account is active.
const importManagedRoles = [UserRole.PI, UserRole.SO];

const supervisorRoleFor = (role: UserRole): UserRole | null => {
    switch (role) {
        case UserRole.PO: return UserRole.PO_SUPERVISOR;
        case UserRole.GMS: return UserRole.GMS_SUPERVISOR;
        case UserRole.ORF_DTR: return UserRole.ORF_DTR_SUPERVISOR;
        default: return null;
    }
};


const externalRoleLabels: Record<number, string> = {
    [ExternalUserRole.AE_DESIGNER]: 'A/E Designer',
    [ExternalUserRole.REVIEWER]: 'Reviewer',
    [ExternalUserRole.PROJECT_MANAGER]: 'Project Manager',
};

const externalRoles = [ExternalUserRole.AE_DESIGNER, ExternalUserRole.REVIEWER, ExternalUserRole.PROJECT_MANAGER];

const externalRoleShortLabels: Record<number, string> = {
    [ExternalUserRole.AE_DESIGNER]: 'Designer',
    [ExternalUserRole.REVIEWER]: 'Reviewer',
    [ExternalUserRole.PROJECT_MANAGER]: 'Project Manager',
};

const emptyUser = (): UserDto => ({
    id: '00000000-0000-0000-0000-000000000000',
    userName: '',
    fullName: '',
    phoneNumber: null,
    firstName: null,
    middleName: null,
    lastName: null,
    email: null,
    externalOrgIds: [],
    userProfiles: [],
    role: UserRole.PO,
    isActive: true,
    primaryProfileId: null,
    grantAssignments: null,
    roleAssignments: [],
});

type AssignmentState = Record<number, string[]>;

const emptyAssignments = (): AssignmentState => ({
    [ExternalUserRole.AE_DESIGNER]: [],
    [ExternalUserRole.REVIEWER]: [],
    [ExternalUserRole.PROJECT_MANAGER]: [],
});

const UserManagement: React.FC = () => {
    const app = useAppContext();
    const [users, setUsers] = useState<UserDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingUser, setEditingUser] = useState<UserDto>(emptyUser());
    const [isNew, setIsNew] = useState(false);
    const [saving, setSaving] = useState(false);
    const [grantOptions, setGrantOptions] = useState<GrantOptionDto[]>([]);
    const [assignments, setAssignments] = useState<AssignmentState>(emptyAssignments());
    const validationGroupRef = useRef<ValidationGroup>(null);

    const isExternal = editingUser.role === UserRole.EXTERNAL_USER;
    const isImportManaged = importManagedRoles.includes(editingUser.role);

    const roleDataSource = useMemo(() => (
        isImportManaged
            ? [{ value: editingUser.role, label: roleLabels[editingUser.role] }]
            : roleOptions
    ), [isImportManaged, editingUser.role]);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        const result = await getUsers();
        if (result?.error) {
            app.errorToast(result.error.message);
        } else if (result?.data) {
            setUsers(result.data);
        }
        setLoading(false);
    }, []);

    const loadGrantOptions = useCallback(async () => {
        const result = await getGrantOptions();
        if (result?.data) {
            setGrantOptions(result.data);
        }
    }, []);

    useEffect(() => {
        loadUsers();
        loadGrantOptions();
    }, []);

    const onAddClick = useCallback(() => {
        setEditingUser(emptyUser());
        setAssignments(emptyAssignments());
        setIsNew(true);
        setDialogVisible(true);
    }, []);

    const onRowClick = useCallback(async (e: any) => {
        if (e.rowType === 'data') {
            const user: UserDto = e.data;
            setEditingUser({ ...user });
            setIsNew(false);

            if (user.role === UserRole.EXTERNAL_USER) {
                const result = await getUserGrantAssignments(user.id);
                if (result?.error) {
                    app.errorToast(result.error.message);
                    setAssignments(emptyAssignments());
                } else if (result?.data) {
                    const state = emptyAssignments();
                    for (const a of result.data) {
                        state[a.role] = a.grantIds;
                    }
                    setAssignments(state);
                } else {
                    setAssignments(emptyAssignments());
                }
            } else {
                setAssignments(emptyAssignments());
            }

            setDialogVisible(true);
        }
    }, []);

    const buildGrantAssignments = useCallback((): ExternalUserGrantAssignmentDto[] => {
        const result: ExternalUserGrantAssignmentDto[] = [];
        for (const role of externalRoles) {
            const grantIds = assignments[role];
            if (grantIds.length > 0) {
                result.push({ role, grantIds });
            }
        }
        return result;
    }, [assignments]);

    const onSave = useCallback(async () => {
        const instance = validationGroupRef.current?.instance;
        if (!instance) return;

        const validationResult = instance.validate();
        if (!validationResult.isValid) {
            if (validationResult.status === 'pending') {
                const complete = await validationResult.complete;
                if (!complete?.isValid) return;
            } else {
                return;
            }
        }

        setSaving(true);
        let result;

        if (isExternal) {
            const dto = { ...editingUser, grantAssignments: buildGrantAssignments() };
            result = isNew
                ? await createExternalUser(dto)
                : await updateExternalUser(editingUser.id, dto);
        } else {
            result = isNew
                ? await createUser(editingUser)
                : await updateUser(editingUser.id, editingUser);
        }

        setSaving(false);

        if (result?.error) {
            app.errorToast(result.error.message);
            return;
        }

        setDialogVisible(false);
        await loadUsers();
    }, [editingUser, isNew, isExternal, loadUsers, buildGrantAssignments]);

    const onCancel = useCallback(() => {
        setDialogVisible(false);
    }, []);

    const updateField = useCallback(<K extends keyof UserDto>(field: K, value: UserDto[K]) => {
        setEditingUser(prev => ({ ...prev, [field]: value }));
    }, []);

    const validateUsernameUnique = useCallback((params: any) => {
        return new Promise<boolean>((resolve) => {
            setTimeout(async () => {
                const excludeId = isNew ? undefined : editingUser.id;
                const exists = await usernameExists(params.value, excludeId);
                resolve(!exists);
            }, 300);
        });
    }, [isNew, editingUser.id]);

    const validateEmailUnique = useCallback((params: any) => {
        if (!params.value) return Promise.resolve(true);
        return new Promise<boolean>((resolve) => {
            setTimeout(async () => {
                const excludeId = isNew ? undefined : editingUser.id;
                const exists = await emailExists(params.value, excludeId);
                resolve(!exists);
            }, 300);
        });
    }, [isNew, editingUser.id]);

    const grantDataSource = useMemo(() => {
        return new DataSource({
            store: new ArrayStore({
                data: grantOptions,
                key: 'id',
            }),
            group: 'fy',
            sort: [{ selector: 'fy', desc: true }],
        });
    }, [grantOptions]);

    const grantDisplayExpr = useCallback((item: GrantOptionDto) => {
        if (!item) return '';
        const inst = item.shortInstitution || item.institution;
        return `${item.grantNumber} - ${inst}`;
    }, []);

    if (!app.loginUser) {
        return <div></div>;
    }

    if (!app.UserIsSuper) {
        return (
            <div className="container-fluid" style={{ padding: '20px' }}>
                <h4>Access Denied</h4>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12" style={{ marginTop: '10px', paddingLeft: '0px', paddingRight: '0px' }}>
                    <div className="grantVisualizationCanvas">
                        <div className="grantVisualizationPanelTop">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div className="panelTitle">
                                    User Management
                                </div>
                                <div style={{ padding: '5px 5px 5px 0' }}>
                                    <DxButton
                                        text="Add User"
                                        type="default"
                                        stylingMode="contained"
                                        onClick={onAddClick}
                                    />
                                </div>
                            </div>
                            <hr />
                            <DataGrid
                                dataSource={users}
                                keyExpr="id"
                                showBorders={true}
                                showRowLines={true}
                                columnAutoWidth={true}
                                wordWrapEnabled={false}
                                noDataText={loading ? "Loading..." : "No users found"}
                                onRowClick={onRowClick}
                                hoverStateEnabled={true}
                                onCellHoverChanged={(e: any) => {
                                    if (e.rowType === 'data') {
                                        e.cellElement.style.cursor = 'pointer';
                                    }
                                }}
                            >
                                <Sorting mode="multiple" />
                                <FilterRow visible={true} />
                                <HeaderFilter visible={true} />
                                <Paging defaultPageSize={20} />

                                <Column dataField="userName" caption="Username" width={180} />
                                <Column dataField="firstName" caption="First Name" width={120} />
                                <Column dataField="lastName" caption="Last Name" width={120} />
                                <Column dataField="email" caption="Email" />
                                <Column
                                    dataField="role"
                                    caption="Role"
                                    width={340}
                                    calculateCellValue={(rowData: UserDto) => {
                                        if (rowData.role === UserRole.EXTERNAL_USER && rowData.grantAssignments?.length) {
                                            const roles = rowData.grantAssignments.map(a => externalRoleShortLabels[a.role]).filter(Boolean).join(', ');
                                            return roles ? `External User (${roles})` : 'External User';
                                        }
                                        return roleLabels[rowData.role] ?? String(rowData.role);
                                    }}
                                    cellRender={(cellData: any) => {
                                        const rowData: UserDto = cellData.data;
                                        if (rowData.role === UserRole.EXTERNAL_USER && rowData.grantAssignments?.length) {
                                            const roles = rowData.grantAssignments.map(a => externalRoleShortLabels[a.role]).filter(Boolean).join(', ');
                                            return <span>External User {roles && <span style={{ fontSize: '0.85em', color: '#666' }}>({roles})</span>}</span>;
                                        }
                                        return <span>{roleLabels[rowData.role] ?? String(rowData.role)}</span>;
                                    }}
                                />
                                <Column
                                    dataField="isActive"
                                    caption="Active"
                                    dataType="boolean"
                                    width={100}
                                />
                                <Column dataField="primaryProfileId" caption="Profile ID" width={120} />
                            </DataGrid>
                        </div>
                    </div>
                </div>
            </div>

            <Popup
                visible={dialogVisible}
                onHiding={onCancel}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title={isNew ? "Add User" : "Edit User"}
                width={isExternal ? 900 : 500}
                height="auto"
                maxHeight="90vh"
            >
                <ValidationGroup ref={validationGroupRef}>
                    <div style={{ display: 'flex', gap: '20px', padding: '10px' }}>
                        {/* Left column: form fields */}
                        <div style={{ flex: isExternal ? '0 0 40%' : '1 1 100%' }}>
                            {/* Role - first field */}
                            <div style={{ marginBottom: '10px' }}>
                                <label>Role *</label>
                                <SelectBox
                                    dataSource={roleDataSource}
                                    displayExpr="label"
                                    valueExpr="value"
                                    value={editingUser.role}
                                    disabled={isImportManaged}
                                    onValueChanged={(e) => updateField('role', e.value)}
                                >
                                    <Validator>
                                        <RequiredRule message="Role is required" />
                                    </Validator>
                                </SelectBox>
                                {isImportManaged && (
                                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                        Account details come from eRA and are refreshed at each login. Only the Active setting can be changed here.
                                    </div>
                                )}
                            </div>

                            {/* Username - internal only */}
                            {!isExternal && (
                                <div style={{ marginBottom: '10px' }}>
                                    <label>Username *</label>
                                    <TextBox
                                        value={editingUser.userName}
                                        onValueChanged={(e) => updateField('userName', e.value)}
                                        maxLength={50}
                                        readOnly={isImportManaged}
                                        validationMessagePosition="bottom"
                                    >
                                        <Validator>
                                            <RequiredRule message="Username is required" />
                                            <StringLengthRule max={50} message="Username must be 50 characters or less" />
                                            <PatternRule
                                                pattern={/^[a-zA-Z0-9@.]+$/}
                                                message="Username may only contain letters, numbers, @, and periods"
                                            />
                                            <AsyncRule
                                                ignoreEmptyValue={true}
                                                message="Username already exists"
                                                validationCallback={validateUsernameUnique}
                                            />
                                        </Validator>
                                    </TextBox>
                                </div>
                            )}

                            {/* Email - required for external, optional for internal */}
                            <div style={{ marginBottom: '10px' }}>
                                <label>Email Address{isExternal ? ' *' : ''}</label>
                                <TextBox
                                    value={editingUser.email ?? ''}
                                    onValueChanged={(e) => updateField('email', e.value || null)}
                                    readOnly={isImportManaged}
                                    validationMessagePosition="bottom"
                                >
                                    <Validator>
                                        {isExternal && <RequiredRule message="Email is required for external users" />}
                                        <EmailRule ignoreEmptyValue={true} message="Email address is invalid" />
                                        <AsyncRule
                                            ignoreEmptyValue={true}
                                            message="Email address already exists"
                                            validationCallback={validateEmailUnique}
                                        />
                                    </Validator>
                                </TextBox>
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <label>First Name</label>
                                <TextBox
                                    value={editingUser.firstName ?? ''}
                                    onValueChanged={(e) => updateField('firstName', e.value || null)}
                                    maxLength={30}
                                    readOnly={isImportManaged}
                                    validationMessagePosition="bottom"
                                >
                                    <Validator>
                                        <StringLengthRule max={30} message="First name must be 30 characters or less" />
                                        <PatternRule
                                            ignoreEmptyValue={true}
                                            pattern={/^[a-zA-Z \-']+$/}
                                            message="First name may only contain letters, spaces, hyphens, and apostrophes"
                                        />
                                    </Validator>
                                </TextBox>
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <label>Last Name</label>
                                <TextBox
                                    value={editingUser.lastName ?? ''}
                                    onValueChanged={(e) => updateField('lastName', e.value || null)}
                                    maxLength={50}
                                    readOnly={isImportManaged}
                                    validationMessagePosition="bottom"
                                >
                                    <Validator>
                                        <StringLengthRule max={50} message="Last name must be 50 characters or less" />
                                        <PatternRule
                                            ignoreEmptyValue={true}
                                            pattern={/^[a-zA-Z \-']+$/}
                                            message="Last name may only contain letters, spaces, hyphens, and apostrophes"
                                        />
                                    </Validator>
                                </TextBox>
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <CheckBox
                                    text="Active"
                                    value={editingUser.isActive}
                                    onValueChanged={(e) => updateField('isActive', e.value)}
                                />
                            </div>

                            {/* Primary Profile ID - internal only */}
                            {!isExternal && (
                                <div style={{ marginBottom: '10px' }}>
                                    <label>Primary Profile ID</label>
                                    <NumberBox
                                        value={editingUser.primaryProfileId ?? undefined}
                                        onValueChanged={(e) => updateField('primaryProfileId', e.value)}
                                        readOnly={isImportManaged}
                                        format="#"
                                        max={9999999999}
                                        min={0}
                                    >
                                        <Validator>
                                            <CustomRule
                                                ignoreEmptyValue={true}
                                                message="Primary Profile ID must be 10 digits or less"
                                                validationCallback={(e: any) => {
                                                    if (e.value == null) return true;
                                                    return String(e.value).length <= 10;
                                                }}
                                            />
                                        </Validator>
                                    </NumberBox>
                                </div>
                            )}

                            {/* Supervisor - only shown for roles that have a supervisor counterpart */}
                            {!isExternal && supervisorRoleFor(editingUser.role) !== null && (
                                <div style={{ marginBottom: '10px' }}>
                                    <CheckBox
                                        text="Supervisor"
                                        value={(editingUser.roleAssignments ?? []).includes(supervisorRoleFor(editingUser.role)!)}
                                        onValueChanged={(e) => {
                                            const matchingRole = supervisorRoleFor(editingUser.role)!;
                                            const current = editingUser.roleAssignments ?? [];
                                            const next = e.value
                                                ? (current.includes(matchingRole) ? current : [...current, matchingRole])
                                                : current.filter(r => r !== matchingRole);
                                            updateField('roleAssignments', next);
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Right column: grant assignments - external only */}
                        {isExternal && (
                            <div style={{ flex: '1 1 60%', borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
                                <label style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>Grant Assignments</label>
                                {externalRoles.map(role => (
                                    <div key={role} style={{ marginBottom: '15px' }}>
                                        <label>{externalRoleLabels[role]}</label>
                                        <TagBox
                                            dataSource={grantDataSource}
                                            grouped={true}
                                            valueExpr="id"
                                            displayExpr={grantDisplayExpr}
                                            value={assignments[role]}
                                            onValueChanged={(e) => {
                                                setAssignments(prev => ({
                                                    ...prev,
                                                    [role]: e.value
                                                }));
                                            }}
                                            searchEnabled={true}
                                            showSelectionControls={true}
                                            maxDisplayedTags={5}
                                            showMultiTagOnly={false}
                                            placeholder="Search and select grants..."
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '0 10px 10px' }}>
                        <DxButton
                            text="Cancel"
                            type="normal"
                            stylingMode="outlined"
                            onClick={onCancel}
                        />
                        <DxButton
                            text={saving ? "Saving..." : "Save"}
                            type="default"
                            stylingMode="contained"
                            disabled={saving}
                            onClick={onSave}
                        />
                    </div>
                </ValidationGroup>
            </Popup>
        </div>
    );
};

export default UserManagement;
